import { useRef, useEffect } from "react";
import HoloCard from "./HoloCard";
import "./HoloCard.css";
import { Hand, X } from "lucide-react";

export default function HoloCardInspect({
  image,
  topLayer,
  foil,
  mask,
  onClose,
  rarity,
  playerId,
  playerSkinId,
  equipped,
  handleEquipSkin,
  showEquip = false,
  collectionName,
  titleUrl,
}) {
  const sceneRef = useRef(null);
  const cardRef = useRef(null);
  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);
  const autoTiltRef = useRef(null);
  const isInteractingRef = useRef(false);
  const lastInteractionRef = useRef(Date.now());

  const handleMouseMove = (e) => {
    isInteractingRef.current = true;
    cancelAnimationFrame(autoTiltRef.current);

    const scene = sceneRef.current;
    const card = cardRef.current;
    if (!scene || !card) return;

    const rect = scene.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;

    // 🔒 clamp to card bounds
    x = clamp(x, 0, rect.width);
    y = clamp(y, 0, rect.height);

    const px = (x / rect.width) * 100;
    const py = (y / rect.height) * 100;

    const smoothPx = Math.min(Math.max(px, 5), 95);
    const smoothPy = Math.min(Math.max(py, 5), 95);

    const rotateX = (smoothPy - 50) / 3;
    const rotateY = (smoothPx - 50) / -3;

    card.style.setProperty("--mx", `${smoothPx}%`);
    card.style.setProperty("--my", `${smoothPy}%`);
    card.style.setProperty("--posx", `${smoothPx}%`);
    card.style.setProperty("--posy", `${smoothPy}%`);
    card.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    lastInteractionRef.current = Date.now();

    const card = cardRef.current;
    if (!card) return;
    card.style.transform = `rotateX(0deg) rotateY(0deg)`;
    card.style.setProperty("--mx", "50%");
    card.style.setProperty("--my", "50%");
    card.style.setProperty("--posx", "50%");
    card.style.setProperty("--posy", "50%");

    resumeAutoTilt();
  };

const handleTouchMove = (e) => {
  isInteractingRef.current = true;
  cancelAnimationFrame(autoTiltRef.current);
  
  // Prevent the page from scrolling while interacting with the card
  if (e.cancelable) e.preventDefault(); 
  
  const touch = e.touches[0];
  if (!touch) return;

  const scene = sceneRef.current;
  const card = cardRef.current;
  if (!scene || !card) return;

  const rect = scene.getBoundingClientRect();
  
  // Increase sensitivity: Use a larger "hit area" or a smaller divisor
  let x = touch.clientX - rect.left;
  let y = touch.clientY - rect.top;

  x = clamp(x, 0, rect.width);
  y = clamp(y, 0, rect.height);

  const px = (x / rect.width) * 100;
  const py = (y / rect.height) * 100;

  // Use a softer clamp or none at all for touch to allow more edge-tilt
  const smoothPx = Math.min(Math.max(px, 0), 100);
  const smoothPy = Math.min(Math.max(py, 0), 100);

  // Divide by 2 (25deg max) instead of 3 (16deg max) for more tilt
  const rotateX = (smoothPy - 50) / 2; 
  const rotateY = (smoothPx - 50) / -2;

  card.style.setProperty("--mx", `${smoothPx}%`);
  card.style.setProperty("--my", `${smoothPy}%`);
  card.style.setProperty("--posx", `${smoothPx}%`);
  card.style.setProperty("--posy", `${smoothPy}%`);
  card.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
};

  const handleTouchEnd = () => {
    lastInteractionRef.current = Date.now();

    const card = cardRef.current;
    if (!card) return;

    card.style.transform = `rotateX(0deg) rotateY(0deg)`;
    card.style.setProperty("--mx", "50%");
    card.style.setProperty("--my", "50%");
    card.style.setProperty("--posx", "50%");
    card.style.setProperty("--posy", "50%");

    resumeAutoTilt();
  };

  const startAutoTilt = () => {
    cancelAnimationFrame(autoTiltRef.current);

    const animate = () => {
      if (isInteractingRef.current) {
        cancelAnimationFrame(autoTiltRef.current);
        return;
      }

      const card = cardRef.current;
      if (!card) return;

      const SPEED = 0.0015; // this is fast
      const t = performance.now() * SPEED;

      const px = 50 + Math.sin(t) * 30;
      const py = 50 + Math.cos(t * 0.8) * 15;

      const rotateX = (py - 50) / 1.5;
      const rotateY = (px - 50) / -1.5;

      card.style.setProperty("--mx", `${50 + (px - 50) * 1.15}%`);
      card.style.setProperty("--my", `${50 + (py - 50) * 1.15}%`);
      card.style.setProperty("--posx", `${px}%`);
      card.style.setProperty("--posy", `${py}%`);
      const shineX = 50 + Math.sin(t * 1.4) * 45;
      const shineY = 50 + Math.cos(t * 1.1) * 45;

      card.style.setProperty("--shine-x", `${shineX}%`);
      card.style.setProperty("--shine-y", `${shineY}%`);

      card.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;

      autoTiltRef.current = requestAnimationFrame(animate);
    };

    autoTiltRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    startAutoTilt();
    return () => cancelAnimationFrame(autoTiltRef.current);
  }, []);

  const resumeAutoTilt = () => {
    setTimeout(() => {
      if (Date.now() - lastInteractionRef.current > 100) {
        isInteractingRef.current = false;
        startAutoTilt();
      }
    }, 100);
  };

  return (
    <div
      className="inspect-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        placeItems: "center",
        zIndex: 1000,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "30px",
        touchAction: "none",
        overscrollBehavior: "contain",
        width: "100vw",
        height: "100vh",
      }}>
      {titleUrl ? (
        <img src={titleUrl} alt={collectionName} style={{ width: "90%", maxWidth: "636px", objectFit: "contain" }} />
      ) : (
        <h2 style={{ color: "white", margin: 0 }}>{collectionName}</h2>
      )}
      <h3 style={{ margin: "0", color: "white", opacity: "0.8", display: "flex", alignItems: "center", gap: "10px", fontSize: "16px" }}>
        <Hand size={16} /> Try moving or hover the card around
      </h3>
      <div
        className="scene"
        ref={sceneRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()} // prevent modal close when moving over card
      >
        <HoloCard
          ref={cardRef}
          image={image}
          topLayer={topLayer}
          foil={foil}
          mask={mask}
          borderRadius={"16px"}
          glow
          rarity={rarity}
          heightTop={360}
        />
      </div>
      {showEquip && playerId && playerSkinId && (
        <button
          disabled={equipped}
          onClick={() => handleEquipSkin(playerId, playerSkinId)}
          style={{
            padding: "8px 20px",
            borderRadius: "8px",
            zIndex: "5",
            fontWeight: "600",
            cursor: equipped ? "default" : "pointer",
            opacity: equipped ? 0.6 : 1,
          }}
          className="edit-btn">
          {equipped ? "Equipped" : "Equip"}
        </button>
      )}
      <button
        onClick={onClose}
        style={{
          padding: "6px 18px",
          borderRadius: "8px",
          fontWeight: "500",
          cursor: "pointer",
          background: "none",
          color: "white",
          border: "none",
        }}
        className="quick-close-btn">
        <X size={38} />
      </button>
    </div>
  );
}
